import React, { createContext, useState, useMemo, useEffect } from 'react';
// IMPORT: MUI Theme Engine
import { createTheme, ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import { useAuth } from './useAuth';
import { isTenantScoped } from '../utils/permissions';

export const ThemeContext = createContext(null);

export const useTheme = () => React.useContext(ThemeContext);

const hexToRgb = (hex) => {
    let result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? `${parseInt(result[1], 16)} ${parseInt(result[2], 16)} ${parseInt(result[3], 16)}` : null;
};

const getContrastColor = (hex) => {
    if (!hex) return '#ffffff';
    // Remove hash if present
    const cleanHex = hex.replace('#', '');
    if (cleanHex.length !== 6) return '#ffffff';
    
    const r = parseInt(cleanHex.substring(0, 2), 16);
    const g = parseInt(cleanHex.substring(2, 4), 16);
    const b = parseInt(cleanHex.substring(4, 6), 16);
    
    // Relative luminance formula
    const yiq = (r * 299 + g * 587 + b * 114) / 1000;
    return yiq >= 128 ? '#000000' : '#ffffff';
};

export const getShapeClasses = (shape) => {
    switch (shape) {
        case 'pill': return 'rounded-full';
        case 'square': return 'rounded-none';
        case 'rounded': default: return 'rounded-md';
    }
};

export const DEFAULT_THEME = {
    mode: 'light',
    primaryColor: '#3b82f6',
    secondaryColor: '#64748b',
    shape: 'rounded',
    borderRadius: 4,
    font: 'Inter',
    sidebarTheme: { bg: null, text: null },
    headerTheme: { bg: null },

    chartTheme: 'default'
};

const ALL_FONTS = ['Inter', 'Poppins', 'Lora', 'Roboto Slab', 'Inconsolata', 'JetBrains Mono', 'Open+Sans'];

export const ThemeProvider = ({ children }) => {
    const { branding, user, token } = useAuth();
    const [theme, setTheme] = useState(DEFAULT_THEME);

    // This effect handles "Precedence Layering" whenever base data changes
    useEffect(() => {
        // Initial / Rehydration logic
        const config = branding?.theme_config || {};
        const userPrefs = user?.theme_settings || {};

        setTheme(prev => ({
            ...DEFAULT_THEME,
            // 1. Layer Branding
            primaryColor: config.theme_color || config.primaryColor || config.accentColor || DEFAULT_THEME.primaryColor,
            secondaryColor: config.secondary_color || config.secondaryColor || DEFAULT_THEME.secondaryColor,
            font: config.font_family || config.font || DEFAULT_THEME.font,
            mode: config.theme_mode || config.mode || DEFAULT_THEME.mode,
            borderRadius: config.border_radius !== undefined ? config.border_radius : DEFAULT_THEME.borderRadius,
            shape: config.shape || (config.border_radius !== undefined ? (config.border_radius === 0 ? 'square' : (config.border_radius > 20 ? 'pill' : 'rounded')) : DEFAULT_THEME.shape),
            
            // 2. Layer User Preferences (OVERRIDE branding)
            ...(userPrefs.accentColor || userPrefs.primaryColor ? { primaryColor: userPrefs.accentColor || userPrefs.primaryColor } : {}),
            ...(userPrefs.secondaryColor ? { secondaryColor: userPrefs.secondaryColor } : {}),
            ...(userPrefs.font ? { font: userPrefs.font } : {}),
            ...(userPrefs.mode ? { mode: userPrefs.mode } : {}),
            ...(userPrefs.borderRadius !== undefined ? { borderRadius: userPrefs.borderRadius } : {}),
            ...(userPrefs.shape ? { shape: userPrefs.shape } : {}),
            
            // Sync specific sub-themes
            sidebarTheme: { 
                ...DEFAULT_THEME.sidebarTheme, 
                ...(config.sidebarTheme || {}), 
                ...(userPrefs.sidebarTheme || {}) 
            },
            headerTheme: { 
                ...DEFAULT_THEME.headerTheme, 
                ...(config.headerTheme || {}), 
                ...(userPrefs.headerTheme || {}) 
            },

            chartTheme: userPrefs.chartTheme || config.chart_theme || DEFAULT_THEME.chartTheme
        }));
    }, [branding, user]);

    const [systemPrefersDark, setSystemPrefersDark] = useState(() => 
        window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches
    );

    useEffect(() => {
        if (!window.matchMedia) return;
        const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
        const handleChange = (e) => setSystemPrefersDark(e.matches);
        mediaQuery.addEventListener('change', handleChange);
        return () => mediaQuery.removeEventListener('change', handleChange);
    }, []);

    const actualMode = theme.mode === 'system' ? (systemPrefersDark ? 'dark' : 'light') : (theme.mode || 'light');

    // Authentication scoping
    const isTenant = !!token && isTenantScoped(user) && !!branding;

    useEffect(() => {
        // 1. Google Fonts Loading
        const activeFontEncoded = theme.font ? theme.font.replace(/ /g, '+') : null;
        const fontsToLoad = [...new Set([...ALL_FONTS, ...(activeFontEncoded ? [activeFontEncoded] : [])])];
        const fontUrl = `https://fonts.googleapis.com/css2?family=${fontsToLoad.join('&family=')}:wght@400;600;700&display=swap`;
        const linkId = 'google-font-stylesheet';
        let link = document.getElementById(linkId);
        if (!link) {
            link = document.createElement('link');
            link.id = linkId;
            link.rel = 'stylesheet';
            document.head.appendChild(link);
        }
        link.href = fontUrl;

        // 2. CSS Variables Application
        // Precedence: User > Tenant > Default (Already handled in theme state)
        const root = window.document.documentElement;
        const primary = theme.primaryColor || '#3b82f6';
        const secondary = theme.secondaryColor || '#64748b';
        const primaryRgb = hexToRgb(primary) || '59 130 246';
        const secondaryRgb = hexToRgb(secondary) || '100 116 139';
        
        root.style.setProperty('--primary-color', primary);
        root.style.setProperty('--primary-rgb', primaryRgb);
        root.style.setProperty('--primary-foreground', getContrastColor(primary));
        root.style.setProperty('--secondary-color', secondary);
        root.style.setProperty('--secondary-rgb', secondaryRgb);
        root.style.setProperty('--accent-color', primary); 
        root.style.setProperty('--accent-rgb', primaryRgb); 
        root.style.setProperty('--font-family', theme.font || "'Inter', sans-serif");
        root.style.setProperty('--border-radius', `${theme.borderRadius ?? 4}px`);
        
        // Sidebar/Header: Only use explicit user/tenant overrides if they exist.
        // Otherwise, derive from actualMode so sidebar always matches the app theme.
        const sbBg = (theme.sidebarTheme?.bg) ? theme.sidebarTheme.bg : (actualMode === 'dark' ? '#0f172a' : '#ffffff');
        const sbText = (theme.sidebarTheme?.text) ? theme.sidebarTheme.text : (actualMode === 'dark' ? '#e2e8f0' : '#1e293b');
        const hdrBg = (theme.headerTheme?.bg) ? theme.headerTheme.bg : (actualMode === 'dark' ? '#1e293b' : '#ffffff');
        
        root.style.setProperty('--sidebar-bg', sbBg);
        root.style.setProperty('--sidebar-text', sbText);
        root.style.setProperty('--header-bg', hdrBg);

        // 3. Theme Mode (Dark/Light class)
        if (actualMode === 'dark') {
            root.classList.add('dark');
        } else {
            root.classList.remove('dark');
        }

        // 4. Tenant Portal Class (Scoping)
        if (isTenant) {
            root.classList.add('tenant-portal');
        } else {
            root.classList.remove('tenant-portal');
        }

        console.log(`💡 [ThemeContext] Applied (Mode: ${theme.mode}, Resolved: ${actualMode}, isTenant: ${isTenant})`);
    }, [theme, user, branding, token, actualMode, isTenant]);

    // --- UNIVERSAL TEXT SIZE REDUCER ---
    // This configures the global MUI theme to be compact by default.
    const muiTheme = useMemo(() => createTheme({
        palette: {
            mode: actualMode,
            primary: { main: isTenant ? (theme.primaryColor || '#3b82f6') : '#3b82f6' },
            secondary: { main: isTenant ? (theme.secondaryColor || '#64748b') : '#64748b' },
            warning: { main: '#f59e0b' }, // Use standard warning color
            ...(actualMode === 'dark' ? {
                background: {
                    default: '#0f172a', // Slate 900 (Matches body)
                    paper: '#1e293b',   // Slate 800 (Matches dark:bg-gray-800ish)
                },
                text: {
                    primary: '#f1f5f9', // Slate 100
                    secondary: '#94a3b8', // Slate 400
                },
            } : {
                // Light mode defaults
                background: {
                    default: '#f8fafc',
                    paper: '#ffffff',
                },
                text: {
                    primary: '#1e293b',
                    secondary: '#64748b',
                },
            }),
        },
        typography: {
            fontFamily: isTenant ? theme.font : "'Inter', sans-serif",
            fontSize: 12, // Global base size reduction (Standard is 14)
        },
        shape: {
            borderRadius: 4, // Standard fixed radius for MUI containers (MuiPaper, MuiBox, etc.)
        },
        components: {
            // Force 'small' size for all users
            MuiButton: { defaultProps: { size: 'small' } },
            MuiIconButton: { defaultProps: { size: 'small' } },
            MuiTextField: { defaultProps: { size: 'small' } },
            MuiSelect: { defaultProps: { size: 'small' } },
            MuiFormControl: { defaultProps: { size: 'small' } },
            MuiChip: { defaultProps: { size: 'small' } },

            // Apply branding-specific radius ONLY if in tenant scope
            ...(isTenant ? {
                MuiButton: {
                    defaultProps: { size: 'small' },
                    styleOverrides: { root: { borderRadius: theme.borderRadius } }
                },
                MuiTextField: {
                    defaultProps: { size: 'small' },
                    styleOverrides: { root: { '& .MuiOutlinedInput-root': { borderRadius: theme.borderRadius } } }
                },
                MuiSelect: {
                    defaultProps: { size: 'small' },
                    styleOverrides: { root: { borderRadius: theme.borderRadius } }
                },
                MuiFormControl: {
                    defaultProps: { size: 'small' },
                    styleOverrides: { root: { borderRadius: theme.borderRadius } }
                },
                MuiChip: {
                    defaultProps: { size: 'small' },
                    styleOverrides: { root: { borderRadius: theme.borderRadius } }
                },
                MuiPaper: {
                    styleOverrides: { root: { borderRadius: theme.borderRadius } }
                }
            } : {}),

            // Granular font reductions
            MuiInputBase: {
                styleOverrides: {
                    root: { fontSize: '0.85rem' }
                }
            },
            MuiMenuItem: {
                styleOverrides: {
                    root: { fontSize: '0.85rem', minHeight: 32 }
                }
            },
            MuiTypography: {
                styleOverrides: {
                    // Scale down default body text
                    body1: { fontSize: '0.85rem' },
                    body2: { fontSize: '0.75rem' }
                }
            }
        }
    }), [actualMode, theme.primaryColor, theme.font, theme.borderRadius, isTenant]);

    const themeValue = useMemo(() => ({ theme, setTheme, branding }), [theme, branding]);

    return (
        <ThemeContext.Provider value={themeValue}>
            {/* Wrap app in MUI Theme Provider to apply the reducer */}
            <MuiThemeProvider theme={muiTheme}>
                {children}
            </MuiThemeProvider>
        </ThemeContext.Provider>
    );
};